import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, fmtCRC, fmtDate, dueLabel, daysUntil, waLink } from '../api';
import { useAuth } from '../AuthContext';
import { useWhatsAppChannel } from '../hooks/useWhatsAppChannel';
import Modal from '../components/Modal';
import SearchSelect from '../components/SearchSelect';
import DueFilter, { dueParams, emptyDue, hasDueFilter } from '../components/DueFilter';
import { IconCash, IconChat, IconCheck, IconSend, IconX } from '../components/Icons';

const empty = {
  clientId: '',
  kind: 'platform',
  unit: 'perfil',
  platformId: '',
  comboId: '',
  accountId: '',
  accountEmail: '',
  accountPassword: '',
  profileName: '',
  pin: '',
  price: '',
  nextDueDate: '',
};

const emptyClient = { name: '', phone: '', email: '' };

const statusBadge = {
  activa: 'bg-emerald-500/20 text-emerald-300',
  vencida: 'bg-red-500/20 text-red-300',
  cancelada: 'bg-slate-500/20 text-slate-400',
};

const dateInput = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const dateWithDay = (year, month, day) => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
};

// Próxima aparición futura del día de corte actual. Si vencía un día 3, por
// ejemplo, conserva ese día aunque lleve más de un mes atrasada.
const nextCutoffDate = (currentDueDate) => {
  const current = currentDueDate ? new Date(currentDueDate) : new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoffDay = Number.isNaN(current.getTime()) ? today.getDate() : current.getDate();
  let next = dateWithDay(today.getFullYear(), today.getMonth(), cutoffDay);
  if (next <= today) next = dateWithDay(today.getFullYear(), today.getMonth() + 1, cutoffDay);
  return dateInput(next);
};

const oneMonthFromToday = () => {
  const today = new Date();
  return dateInput(dateWithDay(today.getFullYear(), today.getMonth() + 1, today.getDate()));
};

const tomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return dateInput(tomorrow);
};

const priceFor = (item, unit) => {
  if (!item) return '';
  if (unit === 'cuenta') return item.accountPrice ?? '';
  return item.profilePrice ?? item.price ?? '';
};
const unitsOf = (item) => {
  if (!item) return [];
  const u = [];
  if (item.profileEnabled ?? true) u.push('perfil');
  if (item.accountEnabled) u.push('cuenta');
  return u.length ? u : ['perfil'];
};

const ACCOUNT_FALLBACK = 'Hola {cliente}. Estos son los datos de tu suscripción de {servicio}:\n\nCorreo: {correo}\nContraseña: {contrasena}\nPerfil: {perfil}\nPIN: {pin}\n\nPróximo pago: {fecha}\nMonto: {monto}\n\n¡Gracias por tu compra!';

// Arma el enlace de WhatsApp con los datos de la cuenta para el cliente
const waSend = (c, user) => waLink(c.phone, user?.whatsapp?.accountTemplate || ACCOUNT_FALLBACK, {
  cliente: c.clientName,
  servicio: c.service,
  correo: c.accountEmail || 'No aplica',
  contrasena: c.accountPassword || 'No aplica',
  perfil: c.profileName || 'No aplica',
  pin: c.pin || 'No aplica',
  fecha: fmtDate(c.nextDueDate),
  monto: fmtCRC(c.price),
  negocio: user?.businessName || user?.name,
}, user?.whatsapp?.countryCode);

export default function Subscriptions() {
  const { user } = useAuth();
  const { instant } = useWhatsAppChannel();
  const [subs, setSubs] = useState([]);
  const loadRequest = useRef(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [clients, setClients] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [combos, setCombos] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('status') || '');
  const [fClient, setFClient] = useState(searchParams.get('clientId') || '');
  // El filtro de vencimientos se puede abrir desde el Dashboard con ?due=vencidas
  const [due, setDue] = useState({
    ...emptyDue,
    preset: searchParams.get('due') || '',
    days: Number(searchParams.get('days')) || emptyDue.days,
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
  });
  const [summary, setSummary] = useState(null);
  const [fPlatform, setFPlatform] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [newClient, setNewClient] = useState(emptyClient);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [deliveryState, setDeliveryState] = useState({ message: '', ok: false });
  const [renewal, setRenewal] = useState(null);
  const [renewalForm, setRenewalForm] = useState({ amount: '', method: 'sinpe', nextDueDate: '', note: '' });
  const [renewalBusy, setRenewalBusy] = useState(false);
  const [renewalError, setRenewalError] = useState('');
  const [dueEditor, setDueEditor] = useState(null);
  const [dueEditorBusy, setDueEditorBusy] = useState(false);
  const [dueEditorError, setDueEditorError] = useState('');
  const [actionNotice, setActionNotice] = useState('');

  const query = useMemo(() => {
    const params = dueParams(due);
    if (filter) params.set('status', filter);
    if (fClient) params.set('clientId', fClient);
    return params.toString();
  }, [filter, due, fClient]);

  const load = () => {
    const request = ++loadRequest.current;
    setLoading(true);
    setLoadError('');
    api(`/subscriptions${query ? `?${query}` : ''}`).then((items) => {
      if (request === loadRequest.current) setSubs(items);
    }).catch((err) => {
      if (request === loadRequest.current) {
        setSubs([]);
        setLoadError(err.message);
      }
    }).finally(() => {
      if (request === loadRequest.current) setLoading(false);
    });
    api(`/subscriptions/summary?days=${due.days}`).then(setSummary).catch(() => {});
  };
  useEffect(() => {
    load();
    // Deja el filtro en la URL para poder compartir o recargar la vista
    setSearchParams(query ? new URLSearchParams(query) : {}, { replace: true });
    return () => { loadRequest.current += 1; };
  }, [query]);
  useEffect(() => {
    api('/clients').then(setClients).catch(() => {});
    api('/platforms').then(setPlatforms).catch(() => {});
    api('/combos').then(setCombos).catch(() => {});
    api('/accounts').then(setAccounts).catch(() => {});
  }, []);

  const openModal = () => {
    setForm(empty);
    setNewClient(emptyClient);
    setError('');
    setDeliveryState({ message: '', ok: false });
    setModal(true);
  };

  const item = form.kind === 'platform' ? platforms.find((p) => p._id === form.platformId) : combos.find((c) => c._id === form.comboId);
  const units = unitsOf(item);

  // Cuentas del Banco elegibles para la plataforma y unidad elegidas
  const bankAccounts = useMemo(() => {
    if (form.kind !== 'platform' || !form.platformId) return [];
    return accounts.filter((a) => a.platformId?._id === form.platformId);
  }, [accounts, form.kind, form.platformId]);

  const isEligible = (a) => (form.unit === 'cuenta' ? a.availableProfiles === a.totalProfiles && !a.fullTaken : a.availableProfiles > 0);
  const hasCupo = form.kind !== 'platform' || bankAccounts.some(isEligible);

  const pickItem = (kind, id) => {
    const it = kind === 'platform' ? platforms.find((p) => p._id === id) : combos.find((c) => c._id === id);
    const u = unitsOf(it)[0] || 'perfil';
    setForm((f) => ({
      ...f,
      kind,
      platformId: kind === 'platform' ? id : '',
      comboId: kind === 'combo' ? id : '',
      unit: u,
      accountId: '',
      accountEmail: '',
      accountPassword: '',
      price: priceFor(it, u),
    }));
  };

  const setUnit = (u) => setForm((f) => ({ ...f, unit: u, accountId: '', accountEmail: '', accountPassword: '', price: priceFor(item, u) }));

  const pickAccount = (id) => {
    const a = accounts.find((x) => x._id === id);
    setForm((f) => ({ ...f, accountId: id, accountEmail: a?.email || f.accountEmail, accountPassword: a?.password || f.accountPassword }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      let client = clients.find((c) => c._id === form.clientId);
      let clientId = form.clientId;

      if (!clientId) {
        const name = newClient.name.trim();
        const phone = newClient.phone.trim();
        if (!name || !phone) throw new Error('Escribe el nombre y WhatsApp del nuevo cliente.');
        client = await api('/clients', {
          method: 'POST',
          body: { name, phone, email: newClient.email.trim() },
        });
        clientId = client._id;
        setClients((current) => [...current, client].sort((a, b) => a.name.localeCompare(b.name)));
        setForm((current) => ({ ...current, clientId }));
      }

      const saved = await api('/subscriptions', {
        method: 'POST',
        body: { ...form, clientId, price: Number(form.price), accountId: form.accountId || undefined, nextDueDate: form.nextDueDate || undefined },
      });
      setCreated({
        id: saved._id,
        clientName: client?.name || saved.clientId?.name || '',
        phone: client?.phone || '',
        service: serviceName(saved),
        accountEmail: form.accountEmail,
        accountPassword: form.accountPassword,
        profileName: form.profileName,
        pin: form.pin,
        price: Number(form.price),
        nextDueDate: saved.nextDueDate,
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const sendCreatedAccount = async () => {
    setDeliveryBusy(true);
    setDeliveryState({ message: '', ok: false });
    try {
      const result = await api(`/whatsapp/subscriptions/${created.id}/account`, { method: 'POST' });
      setDeliveryState({
        message: result.skipped
          ? `La cuenta ya se le había enviado a ${created.clientName}.`
          : `Cuenta enviada directamente a ${created.clientName}.`,
        ok: true,
      });
    } catch (err) {
      setDeliveryState({ message: err.message, ok: false });
    } finally {
      setDeliveryBusy(false);
    }
  };

  const openRenewal = (subscription) => {
    setRenewal(subscription);
    setRenewalForm({
      amount: subscription.price ?? '',
      method: 'sinpe',
      nextDueDate: nextCutoffDate(subscription.nextDueDate),
      note: '',
    });
    setRenewalError('');
  };

  const renew = async (event) => {
    event.preventDefault();
    setRenewalBusy(true);
    setRenewalError('');
    try {
      await api(`/subscriptions/${renewal._id}/renew`, {
        method: 'POST',
        body: { ...renewalForm, amount: Number(renewalForm.amount) },
      });
      setActionNotice(`Pago registrado y vencimiento actualizado para ${renewal.clientId?.name || 'el cliente'}.`);
      setRenewal(null);
      load();
    } catch (err) {
      setRenewalError(err.message);
    } finally {
      setRenewalBusy(false);
    }
  };

  const openDueEditor = (subscription) => {
    setDueEditor({ subscription, nextDueDate: dateInput(subscription.nextDueDate) });
    setDueEditorError('');
  };

  const saveDueDate = async (event) => {
    event.preventDefault();
    setDueEditorBusy(true);
    setDueEditorError('');
    try {
      await api(`/subscriptions/${dueEditor.subscription._id}/due-date`, {
        method: 'PATCH',
        body: { nextDueDate: dueEditor.nextDueDate },
      });
      setActionNotice(`Fecha de vencimiento corregida para ${dueEditor.subscription.clientId?.name || 'el cliente'}.`);
      setDueEditor(null);
      load();
    } catch (err) {
      setDueEditorError(err.message);
    } finally {
      setDueEditorBusy(false);
    }
  };
  const cancel = async (s) => {
    if (!confirm('¿Cancelar esta suscripción?')) return;
    await api(`/subscriptions/${s._id}`, { method: 'PUT', body: { status: 'cancelada' } });
    load();
  };
  const remove = async (s) => {
    if (!confirm('¿Eliminar esta suscripción definitivamente?')) return;
    await api(`/subscriptions/${s._id}`, { method: 'DELETE' });
    load();
  };

  const serviceName = (s) => (s.kind === 'combo' ? s.comboId?.name : s.platformId?.name) || '—';

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subs.filter((s) => {
      if (fClient && s.clientId?._id !== fClient) return false;
      if (fPlatform && s.platformId?._id !== fPlatform) return false;
      if (q && !(s.clientId?.name || '').toLowerCase().includes(q) && !(s.clientId?.phone || '').includes(q) && !serviceName(s).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [subs, fPlatform, search, fClient]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Suscripciones</h1>
        <button className="btn-primary" onClick={openModal}>+ Nueva suscripción</button>
      </div>

      {/* Filtros */}
      <div className="filters-inline flex flex-wrap items-center gap-2">
        {['', 'activa', 'vencida', 'cancelada'].map((st) => (
          <button
            key={st}
            onClick={() => setFilter(st)}
            className={`badge cursor-pointer ${filter === st ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400'}`}
          >
            {st || 'todas'}
          </button>
        ))}
        <select className="input inline-block w-auto" value={fPlatform} onChange={(e) => setFPlatform(e.target.value)}>
          <option value="">Todas las plataformas</option>
          {platforms.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <input className="input inline-block w-auto max-w-xs" placeholder="Buscar cliente o servicio…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="w-full sm:w-72">
          <SearchSelect
            value={fClient}
            onChange={setFClient}
            placeholder="Filtrar por cliente o teléfono…"
            options={[
              { value: '', label: 'Todos los clientes' },
              ...clients.map((c) => ({ value: c._id, label: c.name, hint: c.phone })),
            ]}
          />
        </div>
        {fClient && <button type="button" className="btn-ghost" onClick={() => setFClient('')}>Quitar filtro de cliente</button>}
      </div>

      <DueFilter value={due} onChange={setDue} summary={summary} />
      {loadError && <p className="text-sm text-red-400" role="alert">No se pudieron cargar las suscripciones: {loadError}</p>}

      {(hasDueFilter(due) || filter || fClient || search || fPlatform) && (
        <p className="due-result-count">
          {shown.length} {shown.length === 1 ? 'suscripción encontrada' : 'suscripciones encontradas'} con los filtros aplicados
        </p>
      )}

      {actionNotice && (
        <div className="settings-success flex items-center justify-between gap-3">
          <span><IconCheck size={15} /> {actionNotice}</span>
          <button type="button" onClick={() => setActionNotice('')} aria-label="Cerrar aviso"><IconX size={15} /></button>
        </div>
      )}

      <div className="card overflow-x-auto p-0">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-left text-slate-400">
              <th className="p-3">Cliente</th>
              <th className="p-3">Servicio</th>
              <th className="p-3">Precio</th>
              <th className="p-3">Vence</th>
              <th className="p-3">Estado</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && shown.map((s) => {
              const d = daysUntil(s.nextDueDate);
              const dColor = d == null ? 'text-slate-500' : d < 0 ? 'text-red-400' : d <= 3 ? 'text-amber-400' : 'text-slate-500';
              return (
                <tr key={s._id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="p-3 font-medium">{s.clientId?.name || '—'}</td>
                  <td className="p-3">
                    {serviceName(s)}
                    <span className="ml-1 text-xs text-slate-500">({s.unit === 'cuenta' ? 'cuenta' : 'perfil'})</span>
                    {s.profileName && <span className="text-slate-500"> · {s.profileName}</span>}
                  </td>
                  <td className="p-3">{fmtCRC(s.price)}</td>
                  <td className="p-3">
                    <div className="text-slate-400">{fmtDate(s.nextDueDate)}</div>
                    {s.status !== 'cancelada' && <div className={`text-xs ${dColor}`}>{dueLabel(s.nextDueDate)}</div>}
                    {s.status !== 'cancelada' && (
                      <button
                        type="button"
                        className="mt-1 text-xs font-semibold text-indigo-400 hover:text-indigo-300"
                        onClick={() => openDueEditor(s)}
                      >
                        Editar fecha
                      </button>
                    )}
                  </td>
                  <td className="p-3"><span className={`badge ${statusBadge[s.status]}`}>{s.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {s.status !== 'cancelada' && (
                      <button className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300" onClick={() => openRenewal(s)}><IconCash size={16} /> Renovar</button>
                    )}
                    {s.status !== 'cancelada' && (
                      <button className="ml-3 text-slate-400 hover:text-amber-400" onClick={() => cancel(s)}>Cancelar</button>
                    )}
                    <button className="ml-3 inline-flex text-slate-500 hover:text-red-400" title="Eliminar" onClick={() => remove(s)}><IconX size={16} /></button>
                  </td>
                </tr>
              );
            })}
            {loading ? (
              <tr><td colSpan="6" className="p-6 text-center text-slate-400">Cargando suscripciones…</td></tr>
            ) : shown.length === 0 && (
              <tr>
                <td colSpan="6" className="p-6 text-center text-slate-500">
                  {hasDueFilter(due) ? 'Ninguna suscripción vence en ese rango de fechas.' : 'No hay suscripciones con estos filtros.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={!!renewal}
        title="Registrar pago y renovar"
        subtitle={renewal ? `${renewal.clientId?.name || 'Cliente'} · ${serviceName(renewal)}` : ''}
        dismissable={!renewalBusy}
        onClose={() => !renewalBusy && setRenewal(null)}
      >
        {renewal && (
          <form onSubmit={renew} className="space-y-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-400">Vencimiento actual</span>
                <strong>{fmtDate(renewal.nextDueDate)}</strong>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="field-label">
                Monto pagado
                <input
                  className="input mt-1.5"
                  type="number"
                  min="0"
                  value={renewalForm.amount}
                  onChange={(event) => setRenewalForm({ ...renewalForm, amount: event.target.value })}
                  required
                />
              </label>
              <label className="field-label">
                Método de pago
                <select
                  className="input mt-1.5"
                  value={renewalForm.method}
                  onChange={(event) => setRenewalForm({ ...renewalForm, method: event.target.value })}
                >
                  <option value="sinpe">SINPE</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="otro">Otro</option>
                </select>
              </label>
            </div>

            <label className="field-label">
              Nuevo vencimiento
              <input
                className="input mt-1.5"
                type="date"
                min={tomorrowDate()}
                value={renewalForm.nextDueDate}
                onChange={(event) => setRenewalForm({ ...renewalForm, nextDueDate: event.target.value })}
                required
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setRenewalForm({ ...renewalForm, nextDueDate: nextCutoffDate(renewal.nextDueDate) })}
              >
                Mantener día de corte
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setRenewalForm({ ...renewalForm, nextDueDate: oneMonthFromToday() })}
              >
                Un mes desde hoy
              </button>
            </div>

            <label className="field-label">
              Nota (opcional)
              <input
                className="input mt-1.5"
                value={renewalForm.note}
                onChange={(event) => setRenewalForm({ ...renewalForm, note: event.target.value })}
                placeholder="Referencia o detalle del pago"
              />
            </label>

            {renewalError && <p className="form-error">{renewalError}</p>}
            <div className="flex gap-2">
              <button type="button" className="btn-ghost flex-1" disabled={renewalBusy} onClick={() => setRenewal(null)}>Cancelar</button>
              <button type="submit" className="btn-success flex-1" disabled={renewalBusy}>
                <IconCash size={16} /> {renewalBusy ? 'Registrando…' : 'Registrar y renovar'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={!!dueEditor}
        title="Corregir fecha de vencimiento"
        subtitle={dueEditor ? `${dueEditor.subscription.clientId?.name || 'Cliente'} · ${serviceName(dueEditor.subscription)}` : ''}
        dismissable={!dueEditorBusy}
        onClose={() => !dueEditorBusy && setDueEditor(null)}
      >
        {dueEditor && (
          <form onSubmit={saveDueDate} className="space-y-4">
            <p className="text-sm text-slate-400">
              Este cambio no registra ningún pago. La suscripción saldrá de vencidas automáticamente si eliges hoy o una fecha futura.
            </p>
            <label className="field-label">
              Fecha correcta
              <input
                className="input mt-1.5"
                type="date"
                value={dueEditor.nextDueDate}
                onChange={(event) => setDueEditor({ ...dueEditor, nextDueDate: event.target.value })}
                required
              />
            </label>
            {dueEditorError && <p className="form-error">{dueEditorError}</p>}
            <div className="flex gap-2">
              <button type="button" className="btn-ghost flex-1" disabled={dueEditorBusy} onClick={() => setDueEditor(null)}>Cancelar</button>
              <button type="submit" className="btn-primary flex-1" disabled={dueEditorBusy}>
                {dueEditorBusy ? 'Guardando…' : 'Guardar fecha'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={modal} title={created ? 'Suscripción creada' : 'Nueva suscripción'} onClose={() => { setModal(false); setForm(empty); setNewClient(emptyClient); setCreated(null); setDeliveryState({ message: '', ok: false }); }}>
        {created ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-300">La suscripción de <b>{created.service}</b> para <b>{created.clientName}</b> se creó correctamente.</p>
          <div className="space-y-0.5 rounded-lg bg-slate-800/60 p-3 text-xs text-slate-300">
            {created.accountEmail && <div>Correo: {created.accountEmail}</div>}
            {created.accountPassword && <div>Contraseña: {created.accountPassword}</div>}
            {created.profileName && <div>Perfil: {created.profileName}</div>}
            {created.pin && <div>PIN: {created.pin}</div>}
            <div>Precio: {fmtCRC(created.price)}/mes</div>
            <div>Próximo pago: {fmtDate(created.nextDueDate)}</div>
          </div>
          {created.phone ? instant ? (
            <button type="button" onClick={sendCreatedAccount} disabled={deliveryBusy || deliveryState.ok} className="btn w-full bg-emerald-600 text-white hover:bg-emerald-500">
              {deliveryState.ok ? <IconCheck size={16} /> : <IconSend size={16} />}
              {deliveryState.ok ? 'Cuenta enviada' : deliveryBusy ? 'Enviando desde tu número…' : 'Enviar cuenta al instante'}
            </button>
          ) : (
            <a href={waSend(created, user)} target="_blank" rel="noreferrer" className="btn w-full bg-emerald-600 text-white hover:bg-emerald-500">
              <IconChat size={16} /> Abrir cuenta en WhatsApp
            </a>
          ) : (
            <p className="text-xs text-amber-400">Este cliente no tiene número de WhatsApp registrado.</p>
          )}
          {deliveryState.message && <p className={deliveryState.ok ? 'settings-success' : 'form-error'}>{deliveryState.message}</p>}
          <div className="flex gap-2">
            <button type="button" className="btn-ghost flex-1" onClick={() => { setForm(empty); setNewClient(emptyClient); setCreated(null); setDeliveryState({ message: '', ok: false }); }}>Crear otra</button>
            <button type="button" className="btn-primary flex-1" onClick={() => { setModal(false); setForm(empty); setNewClient(emptyClient); setCreated(null); setDeliveryState({ message: '', ok: false }); }}>Listo</button>
          </div>
        </div>
        ) : (
        <form onSubmit={save} className="space-y-3">
          <div className="subscription-client-picker">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <label className="block text-xs font-semibold text-slate-400">Cliente</label>
              {form.clientId && (
                <button
                  type="button"
                  className="text-xs font-semibold text-indigo-400 hover:text-indigo-300"
                  onClick={() => setForm({ ...form, clientId: '' })}
                >
                  Crear cliente nuevo
                </button>
              )}
            </div>
            <SearchSelect
              value={form.clientId}
              onChange={(v) => setForm({ ...form, clientId: v })}
              placeholder="Selecciona un cliente existente…"
              options={clients.map((c) => ({ value: c._id, label: c.name, hint: c.phone }))}
            />

            {!form.clientId && (
              <div className="inline-client-form">
                <div className="inline-client-form__heading">
                  <div>
                    <strong>Nuevo cliente</strong>
                    <p>Se guardará automáticamente al crear la suscripción.</p>
                  </div>
                  <span>Nuevo</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className="input"
                    placeholder="Nombre completo"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    required
                  />
                  <input
                    className="input"
                    placeholder="WhatsApp"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    required
                  />
                  <input
                    className="input sm:col-span-2"
                    type="email"
                    placeholder="Correo (opcional)"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...empty, clientId: form.clientId, kind: 'platform' })} className={form.kind === 'platform' ? 'btn-primary flex-1' : 'btn-ghost flex-1'}>Plataforma</button>
            <button type="button" onClick={() => setForm({ ...empty, clientId: form.clientId, kind: 'combo' })} className={form.kind === 'combo' ? 'btn-primary flex-1' : 'btn-ghost flex-1'}>Combo</button>
          </div>

          {form.kind === 'platform' ? (
            <SearchSelect
              value={form.platformId}
              onChange={(v) => pickItem('platform', v)}
              required
              placeholder="Plataforma…"
              options={platforms.map((p) => ({ value: p._id, label: p.name }))}
            />
          ) : (
            <SearchSelect
              value={form.comboId}
              onChange={(v) => pickItem('combo', v)}
              required
              placeholder="Combo…"
              options={combos.map((c) => ({ value: c._id, label: c.name }))}
            />
          )}

          {item && (
            <div>
              <label className="mb-1 block text-xs text-slate-400">¿Cómo se vende?</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setUnit('perfil')} className={form.unit === 'perfil' ? 'btn-primary flex-1' : 'btn-ghost flex-1'}>Perfil</button>
                <button type="button" onClick={() => setUnit('cuenta')} className={form.unit === 'cuenta' ? 'btn-primary flex-1' : 'btn-ghost flex-1'}>Cuenta completa</button>
              </div>
              {!units.includes(form.unit) && (
                <p className="mt-1 text-xs text-amber-400">
                  Este {form.kind === 'combo' ? 'combo' : 'plataforma'} no tiene ese modo configurado en el Catálogo; ajusta el precio a mano.
                </p>
              )}
            </div>
          )}

          {/* Banco de Cuentas (solo para plataformas) */}
          {form.kind === 'platform' && form.platformId && (
            bankAccounts.length === 0 || !hasCupo ? (
              <p className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-sm text-red-300">
                No hay cupo en el Banco para esta plataforma. Agrega cuentas o perfiles en el Banco de Cuentas.
              </p>
            ) : (
              <SearchSelect
                value={form.accountId}
                onChange={pickAccount}
                required
                placeholder="Cuenta del Banco…"
                options={bankAccounts.map((a) => ({
                  value: a._id,
                  label: a.email || (a.kind === 'perfiles' ? 'Perfiles sueltos' : 'Cuenta'),
                  hint: isEligible(a) ? `${a.availableProfiles} libre(s)` : 'sin cupo',
                  disabled: !isEligible(a),
                }))}
              />
            )
          )}

          <input className="input" type="number" placeholder="Precio mensual (₡)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required min="0" />
          <div>
            <label className="mb-1 block text-xs text-slate-400">Próximo vencimiento (vacío = en 1 mes)</label>
            <input className="input" type="date" value={form.nextDueDate} onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })} />
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-400">Datos de la cuenta (perfil/PIN)</summary>
            <div className="mt-2 space-y-2">
              <input className="input" placeholder="Email de la cuenta" value={form.accountEmail} onChange={(e) => setForm({ ...form, accountEmail: e.target.value })} />
              <input className="input" placeholder="Contraseña de la cuenta" value={form.accountPassword} onChange={(e) => setForm({ ...form, accountPassword: e.target.value })} />
              <div className="flex gap-2">
                <input className="input" placeholder="Perfil" value={form.profileName} onChange={(e) => setForm({ ...form, profileName: e.target.value })} />
                <input className="input" placeholder="PIN" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
              </div>
            </div>
          </details>

          {error && <p className="text-sm text-red-400">{error}</p>}
          <button className="btn-primary w-full" disabled={form.kind === 'platform' && !hasCupo}>Crear suscripción</button>
        </form>
        )}
      </Modal>
    </div>
  );
}
